import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import type { DateView } from '@mui/x-date-pickers';
import type { ChartSettings } from 'models/analytics/chartSettings';
import type { FC } from 'react';

interface ChartSettingsDialogProps {
  open: boolean;
  title: string;
  settings: ChartSettings;
  onClose: () => void;
  onSave: (settings: ChartSettings) => void;
}

const ChartSettingsDialog: FC<ChartSettingsDialogProps> = ({
  open,
  title,
  settings,
  onClose,
  onSave,
}) => {
  const [localSettings, setLocalSettings] = useState<ChartSettings>(settings);

  const pickerView: DateView[] = useMemo(
    () => (localSettings.groupBy === 'month' ? ['month', 'year'] : ['year']),
    [localSettings]
  );

  const handleGroupingChange = (e: SelectChangeEvent<'month' | 'year'>): void => {
    const value = e.target.value as 'month' | 'year';
    if (value === 'year') {
      setLocalSettings({
        ...localSettings,
        groupBy: value,
        dateRange: {
          from: new Date(localSettings.dateRange.from.getFullYear(), 0, 1, 0, 0, 0, 0),
          to: new Date(localSettings.dateRange.to.getFullYear(), 0, 1, 0, 0, 0, 0),
        },
      });
    } else {
      setLocalSettings({
        ...localSettings,
        groupBy: value,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Группировка</InputLabel>
            <Select
              value={localSettings.groupBy}
              label='Группировка'
              onChange={handleGroupingChange}>
              <MenuItem value='month'>По месяцам</MenuItem>
              <MenuItem value='year'>По годам</MenuItem>
            </Select>
          </FormControl>

          <DatePicker
            label='Дата от'
            value={dayjs(localSettings.dateRange.from)}
            openTo={localSettings.groupBy}
            views={pickerView}
            onChange={(date) => {
              setLocalSettings({
                ...localSettings,
                dateRange: {
                  ...localSettings.dateRange,
                  from: date?.toDate() ?? new Date(),
                },
              });
            }}
          />

          <DatePicker
            label='Дата до'
            value={dayjs(localSettings.dateRange.to)}
            openTo={localSettings.groupBy}
            views={pickerView}
            onChange={(date) => {
              setLocalSettings({
                ...localSettings,
                dateRange: {
                  ...localSettings.dateRange,
                  to: date?.toDate() ?? new Date(),
                },
              });
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          onClick={() => {
            onSave(localSettings);
          }}
          variant='contained'>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartSettingsDialog;
